/**
 * ChatPanel — Chat-only sidebar for the canvas layout (320px).
 *
 * Stripped down to just conversation + activity log. All node inspection
 * (code, data preview, schema diff, promote/discard) is now handled by
 * the full-width NodeInspectorPanel below the canvas.
 *
 * Behavior:
 *   - selectedNode=null → general exploration chat (full message history)
 *   - selectedNode set  → header shows node context, messages scoped
 */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Explore as ExploreIcon,
  ListAlt as ActivityLogIcon,
  Chat as ChatIcon,
  Build as ReviewIcon,
  Close as CloseIcon,
  ChevronRight as CollapseChatIcon,
  TravelExplore as ProfileIcon,
  CleaningServices as CleanIcon,
  AccountTree as MapIcon,
  FindReplace as DedupeIcon,
  Schema as SchemaStarterIcon,
} from '@mui/icons-material';
import Editor from '@monaco-editor/react';
import ChatMessage from './ChatMessage';
import ActivityLogView from './ActivityLogView';
import ContextualSuggestions from './ContextualSuggestions';
import type { Suggestion } from './ContextualSuggestions';
import ProgressStrip from './progress/ProgressStrip';
import ProgressSheet from './progress/ProgressSheet';
import { useNotifications, isMappingEngineLogLine } from '../../context/NotificationContext';
import {
  useSendWorkspaceMessage,
  useWorkspaceMessages,
  useDiscardOperation,
  useSetOperationExploratory,
  useCreateOperation,
  useWorkspaceTables,
} from '../../hooks/useWorkspaceSession';
import type { WorkspaceStreamState } from '../../hooks/useWorkspaceSession';
import type { WorkspaceMessage, WorkspaceOperation } from '../../services/workspaceApi';
import type { PipelineNode } from './DAGCanvas';
import { useQueryClient } from 'react-query';
import type { FieldMapping, MappingResponse } from '../../services/api';
import { apiService } from '../../services/api';
import type { FeedbackEntry } from '../../canvas/inspector-tabs/types';
import { MarkdownDisplay } from '../common/MarkdownDisplay';
import { getApiErrorMessage } from '../../services/apiErrors';
import { WORKSPACE_MONACO_EDITOR_DEFAULTS } from './workspaceMonacoDefaults';
import { getNodeColor, getNodeIcon } from './nodeConstants';

// ── Types ────────────────────────────────────────────────────────────

export interface MappingContext {
  mapping: FieldMapping;
  mappingResponse: MappingResponse;
}

interface ChatPanelProps {
  sessionId: string;
  batchId?: string;
  batchFileName?: string;
  /** When set, header shows node context + messages are scoped. */
  selectedNode: PipelineNode | null;
  /** Called when user clicks close on node context header. */
  onDeselectNode: () => void;
  /** All operations for Activity Log tab. */
  operations: WorkspaceOperation[];
  /** SSE stream state for status indicators. */
  stream: WorkspaceStreamState;
  /** External message to send programmatically (e.g., from "Ask agent about column"). */
  pendingMessage?: string | null;
  /** Called after pendingMessage has been consumed so parent can clear it. */
  onPendingMessageConsumed?: () => void;
  /** Whether the agent is busy (used to gate external sends). */
  isBusy?: boolean;
  /** Contextual action capsules shown above the input area. */
  contextualSuggestions?: Suggestion[];
  /** Called when user clicks "Open Mapping Editor" in a mapping_complete CTA card. */
  onOpenEditor?: () => void;
  /** When true, hide the SQL/Python mode toggle (e.g., in mapping context). */
  hideModes?: boolean;
  /** Opens the Generate Mapping dialog (schema selector + mode picker). */
  onOpenGenerateMapping?: () => void;
  /** When true, mapping has been triggered (hides starters even if no messages sent). */
  mappingActive?: boolean;
  /** Mapping mode: 'engine' or 'iterative'. */
  mappingMode?: string;
  /** Number of target tables actually produced by the mapping engine (from recipe). */
  mappingTargetCount?: number;
  /** Called when user clicks a tool_result bar to focus the corresponding canvas node. */
  onFocusNode?: (operationId: string) => void;
  /** Active mapping context for field-level feedback (from MappingContextPanel). */
  activeMappingContext?: MappingContext | null;
  /** Called after a successful mapping feedback submission. */
  onMappingFeedbackSuccess?: () => void;
  /** Optional collapse handler — when provided the chat header renders a
   *  chevron-right button on the right edge. Lets the host fold the panel
   *  without burning a separate row above the header. */
  onCollapse?: () => void;
  /** Workspace export lifecycle status (validating | exporting | …). */
  destinationExportStatus?: string | null;
}

// ── Constants ────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; color: 'default' | 'success' | 'error' | 'info' | 'warning' }> = {
  pending: { label: 'Pending', color: 'default' },
  running: { label: 'Running', color: 'info' },
  completed: { label: 'Completed', color: 'success' },
  failed: { label: 'Failed', color: 'error' },
  awaiting_review: { label: 'Awaiting Review', color: 'warning' },
};

// ── Starter suggestions (shown when session has no messages yet) ─────

interface StarterSuggestion {
  icon: React.ReactElement;
  label: string;
  color: string;
  /** If set, sends this as a chat message. */
  prompt?: string;
  /** If set, calls this callback instead of sending a message. */
  action?: () => void;
}

function buildStarterSuggestions(opts?: { onOpenGenerateMapping?: () => void }): StarterSuggestion[] {
  return [
    {
      icon: <ProfileIcon sx={{ fontSize: 18 }} />,
      label: 'Explore Data',
      prompt: 'Explore and profile the source data. Show me a summary of each table, key columns, data quality issues, and anything unusual.',
      color: '#3b82f6',
    },
    {
      icon: <CleanIcon sx={{ fontSize: 18 }} />,
      label: 'Clean & Standardize',
      prompt: 'Analyze the data for quality issues and propose a cleaning and standardization plan. Look for nulls, inconsistent formats, typos, and outliers.',
      color: '#059669',
    },
    {
      icon: <DedupeIcon sx={{ fontSize: 18 }} />,
      label: 'Deduplicate',
      prompt: 'Find and resolve duplicate or near-duplicate records across the source tables. Propose a deduplication strategy.',
      color: '#d97706',
    },
    {
      icon: <SchemaStarterIcon sx={{ fontSize: 18 }} />,
      label: 'Map to Schema',
      color: '#7c3aed',
      action: opts?.onOpenGenerateMapping,
      // Fallback prompt if dialog callback not provided
      ...(!opts?.onOpenGenerateMapping && {
        prompt: 'Help me select a target schema and generate field mappings for the source data.',
      }),
    },
  ];
}

// ── Component ────────────────────────────────────────────────────────

const ChatPanel: React.FC<ChatPanelProps> = ({
  sessionId,
  batchId,
  batchFileName,
  selectedNode,
  onDeselectNode,
  operations,
  stream,
  pendingMessage,
  onPendingMessageConsumed,
  isBusy,
  contextualSuggestions = [],
  onOpenEditor,
  hideModes,
  onOpenGenerateMapping,
  mappingActive,
  mappingMode,
  mappingTargetCount,
  onFocusNode,
  activeMappingContext,
  onMappingFeedbackSuccess,
  onCollapse,
  destinationExportStatus,
}) => {
  const theme = useTheme();
  const { pipelineProgressMap, exportProgressMap } = useNotifications();
  // Pipeline progress (profiling, semantic summary, etc.) is still scoped by
  // batchId and lives in the global notifications stream. Mapping-specific
  // progress has been replaced by the unified per-session progress channel
  // on ``stream.progress`` (rendered via ProgressStrip / ProgressSheet).
  const pipelineProgress = batchId ? pipelineProgressMap[batchId] ?? null : null;
  const exportProgress = batchId ? exportProgressMap[batchId] : undefined;
  const showPipelineProgress = useMemo(() => {
    if (!pipelineProgress) return false;
    // Mapping engine ticks belong on ProgressStrip; NotificationContext also
    // excludes/clears them from the legacy in-thread slot at the source.
    return !isMappingEngineLogLine(pipelineProgress.logLine);
  }, [pipelineProgress]);
  const showExportProgress = useMemo(() => {
    if (exportProgress) return true;
    return destinationExportStatus === 'validating' || destinationExportStatus === 'exporting';
  }, [exportProgress, destinationExportStatus]);
  const exportProgressPercent = exportProgress?.progressPercent
    ?? (exportProgress && exportProgress.tablesTotal > 0
      ? Math.round((exportProgress.tablesCompleted / exportProgress.tablesTotal) * 100)
      : undefined);
  const exportProgressLabel = destinationExportStatus === 'validating'
    ? 'Validating export compatibility…'
    : exportProgress?.logLine
      || (exportProgress && exportProgress.tablesTotal > 0
        ? `Exporting… ${exportProgress.tablesCompleted}/${exportProgress.tablesTotal} tables`
        : 'Exporting…');
  const [input, setInput] = useState('');
  const [activeTab, setActiveTab] = useState<'chat' | 'activity'>('chat');
  const [progressExpanded, setProgressExpanded] = useState(false);
  const [optimisticMessages, setOptimisticMessages] = useState<WorkspaceMessage[]>([]);

  const starterSuggestions = useMemo(
    () => buildStarterSuggestions({ onOpenGenerateMapping }),
    [onOpenGenerateMapping],
  );
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const [pendingUpload, setPendingUpload] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Code input mode state ──────────────────────────────────────
  const [inputMode, setInputMode] = useState<'chat' | 'sql' | 'python'>('chat');
  const [sqlCodeInput, setSqlCodeInput] = useState('');
  const [pythonCodeInput, setPythonCodeInput] = useState('');
  // Active code input derived from mode
  const codeInput = inputMode === 'sql' ? sqlCodeInput : pythonCodeInput;
  const setCodeInput = inputMode === 'sql' ? setSqlCodeInput : setPythonCodeInput;
  const [codeOutputName, setCodeOutputName] = useState('transform_1');
  const [codeExploratory, setCodeExploratory] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [pythonInputTable, setPythonInputTable] = useState('');

  // ── Mapping feedback state ────────────────────────────────────
  const [feedbackType, setFeedbackType] = useState<'question' | 'regenerate_request'>('question');
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackHistory, setFeedbackHistory] = useState<FeedbackEntry[]>([]);
  const feedbackEndRef = useRef<HTMLDivElement>(null);

  // Seed feedback history when mapping context changes
  const activeMappingId = activeMappingContext?.mapping?.mapping_id;
  useEffect(() => {
    if (!activeMappingContext) {
      setFeedbackHistory([]);
      return;
    }
    const allHistory = (activeMappingContext.mappingResponse?.metadata as Record<string, Record<string, unknown>>)?.feedback_history;
    const history: FeedbackEntry[] = (allHistory as Record<string, FeedbackEntry[]>)?.[activeMappingContext.mapping.mapping_id] ?? [];
    setFeedbackHistory(Array.isArray(history) ? history : []);
  }, [activeMappingId, activeMappingContext?.mappingResponse?.metadata]); // eslint-disable-line

  // Auto-scroll feedback history
  useEffect(() => {
    feedbackEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [feedbackHistory]);

  const queryClient = useQueryClient();

  const handleMappingFeedbackSend = useCallback(async (messageText: string) => {
    if (!activeMappingContext || !messageText.trim()) return;
    const { mapping, mappingResponse } = activeMappingContext;
    if (!mappingResponse?.recipe_id) return;

    const submittedMessage = messageText.trim();
    const submittedType = feedbackType;

    // Optimistic: show user message immediately
    const optimisticId = crypto.randomUUID();
    setFeedbackHistory(prev => [...prev, {
      id: optimisticId,
      feedback_type: submittedType,
      user_message: submittedMessage,
      llm_response: '',  // empty until AI responds
      timestamp: new Date().toISOString(),
    }]);
    setFeedbackLoading(true);

    try {
      const data = await apiService.submitMappingFeedback(
        mappingResponse.batch_id,
        mapping.mapping_id,
        {
          recipe_id: mappingResponse.recipe_id,
          feedback_type: submittedType,
          message: submittedMessage,
          actor: 'web-ui',
        }
      );
      const backendHistory: FeedbackEntry[] | undefined = data.metadata?.history as FeedbackEntry[] | undefined;
      if (backendHistory && Array.isArray(backendHistory)) {
        setFeedbackHistory(backendHistory);
      } else {
        // Replace the optimistic entry with the real response
        setFeedbackHistory(prev => prev.map(entry =>
          entry.id === optimisticId
            ? { ...entry, llm_response: data.llm_response || 'No response.' }
            : entry
        ));
      }

      // After regeneration, invalidate mapping + canvas queries so the table updates
      if (submittedType === 'regenerate_request') {
        await queryClient.invalidateQueries(['mappings', mappingResponse.batch_id]);
        void queryClient.invalidateQueries(['canvas-graph', mappingResponse.batch_id]);
      }

      onMappingFeedbackSuccess?.();
    } catch {
      // Replace the optimistic entry with an error response
      setFeedbackHistory(prev => prev.map(entry =>
        entry.id === optimisticId
          ? { ...entry, llm_response: 'Failed to submit feedback. Please try again.' }
          : entry
      ));
    } finally {
      setFeedbackLoading(false);
    }
  }, [activeMappingContext, feedbackType, onMappingFeedbackSuccess, queryClient]);

  const sendMessage = useSendWorkspaceMessage(sessionId);
  const sseOpts = { sseConnected: stream.connected };
  const { data: allMessages = [], isLoading: loadingMessages } = useWorkspaceMessages(sessionId, sseOpts);
  const discard = useDiscardOperation(sessionId);
  const setExploratory = useSetOperationExploratory(sessionId);
  const createOp = useCreateOperation(sessionId);
  const { data: wsTables = [] } = useWorkspaceTables(sessionId, sseOpts);

  const hasOperations = operations.length > 0;
  const [chatNodeHeaderDismissed, setChatNodeHeaderDismissed] = useState(false);
  useEffect(() => {
    setChatNodeHeaderDismissed(false);
  }, [selectedNode?.stepId]);
  const isNodeMode = selectedNode !== null && !chatNodeHeaderDismissed;
  // Parent passes isBusy only for engine mapping runs; agent-driven flows keep chat open.
  const chatLocked = !!isBusy;

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [allMessages, optimisticMessages, sendMessage.isLoading]);

  // Reset to chat tab when node changes
  useEffect(() => {
    setActiveTab('chat');
  }, [selectedNode?.stepId]);

  // ── Programmatic send (for "Ask agent about column" etc.) ────────
  const pendingConsumedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!pendingMessage || sendMessage.isLoading || chatLocked) return;
    // Guard against double-send (React may re-run the effect before parent clears pendingMessage)
    if (pendingConsumedRef.current === pendingMessage) return;
    pendingConsumedRef.current = pendingMessage;

    const text = pendingMessage;
    onPendingMessageConsumed?.();

    // Create optimistic user message
    const tempId = `temp-${Date.now()}`;
    const tempMsg: WorkspaceMessage = {
      message_id: tempId,
      session_id: sessionId,
      role: 'user',
      content: text,
      metadata: {},
    };
    setOptimisticMessages((prev) => [...prev, tempMsg]);

    sendMessage.mutateAsync(text).then(
      () => {
        setOptimisticMessages([]);
        pendingConsumedRef.current = null;
      },
      () => {
        setOptimisticMessages((prev) =>
          prev.map((m) =>
            m.message_id === tempId
              ? { ...m, metadata: { ...m.metadata, failed: true } }
              : m,
          ),
        );
        pendingConsumedRef.current = null;
      },
    );
  }, [pendingMessage, sendMessage, sessionId, chatLocked, onPendingMessageConsumed]);

  // ── Send handler ──────────────────────────────────────────────────

  const handleSend = useCallback(async (directMessage?: string) => {
    const messageText = directMessage || input;
    if (!messageText.trim() || sendMessage.isLoading || chatLocked) return;

    // Route to mapping feedback API when mapping context is active
    if (activeMappingContext) {
      setInput('');
      await handleMappingFeedbackSend(messageText);
      return;
    }

    const raw = messageText.trim();
    let text = isNodeMode && selectedNode
      ? `[Re: ${selectedNode.title || selectedNode.stepType}] ${raw}`
      : raw;

    // Prepend upload context if a file was just uploaded
    if (pendingUpload) {
      text = `${pendingUpload} ${text}`;
      setPendingUpload(null);
    }

    setInput('');

    const tempId = `temp-${Date.now()}`;
    const tempMsg: WorkspaceMessage = {
      message_id: tempId,
      session_id: sessionId,
      role: 'user',
      content: text,
      metadata: {},
    };
    setOptimisticMessages((prev) => [...prev, tempMsg]);

    try {
      await sendMessage.mutateAsync(text);
      setOptimisticMessages([]);
    } catch {
      // Mark the optimistic message as failed so the user sees an error indicator
      setOptimisticMessages((prev) =>
        prev.map((m) =>
          m.message_id === tempId
            ? { ...m, metadata: { ...m.metadata, failed: true } }
            : m,
        ),
      );
    }
  }, [input, sessionId, sendMessage, isNodeMode, selectedNode, pendingUpload, activeMappingContext, handleMappingFeedbackSend, chatLocked]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── Code mode submit handler ───────────────────────────────────
  const handleCodeSubmit = useCallback(async () => {
    if (!codeInput.trim() || !codeOutputName.trim()) return;
    setCodeError(null);
    try {
      const configKey = inputMode === 'sql' ? 'sql' : 'function_code';
      // Infer input_refs from SQL or use selected table for Python
      const inputRefs: string[] = [];
      if (inputMode === 'sql') {
        const tableRefs = codeInput.match(/(?:FROM|JOIN)\s+(\w+)/gi);
        if (tableRefs) {
          const tables = tableRefs.map((ref) => ref.replace(/^(?:FROM|JOIN)\s+/i, ''));
          const wsNames = new Set(wsTables.map((t) => t.name));
          tables.forEach((t) => { if (wsNames.has(t) && !inputRefs.includes(t)) inputRefs.push(t); });
        }
      } else if (inputMode === 'python' && pythonInputTable) {
        inputRefs.push(pythonInputTable);
      }
      await createOp.mutateAsync({
        operation_type: inputMode === 'sql' ? 'sql_transform' : 'python_transform',
        config: { [configKey]: codeInput },
        output_name: codeOutputName,
        input_refs: inputRefs,
        exploratory: codeExploratory,
      });
      // Reset to chat mode on success — clear the active mode's state
      if (inputMode === 'sql') setSqlCodeInput('');
      else setPythonCodeInput('');
      setInputMode('chat');
    } catch (err: unknown) {
      setCodeError(getApiErrorMessage(err));
    }
  }, [codeInput, codeOutputName, codeExploratory, inputMode, createOp, wsTables, pythonInputTable]);

  const handleSwitchMode = useCallback((mode: 'chat' | 'sql' | 'python') => {
    setCodeError(null);
    if (mode === inputMode) {
      // Toggle back to chat
      setInputMode('chat');
    } else {
      setInputMode(mode);
      // Set scaffold for the target mode if it's empty (each mode has independent state)
      const targetInput = mode === 'sql' ? sqlCodeInput : pythonCodeInput;
      const targetSetter = mode === 'sql' ? setSqlCodeInput : setPythonCodeInput;
      if (!targetInput) {
        targetSetter(mode === 'sql'
          ? '-- Write SQL against workspace tables\nSELECT *\nFROM \nWHERE 1=1'
          : "# Input: df (pandas DataFrame)\nresult = df");
      }
      // Generate output name
      const existingNames = new Set(wsTables.map((t) => t.name));
      let n = 1;
      while (existingNames.has(`transform_${n}`)) n++;
      setCodeOutputName(`transform_${n}`);
    }
  }, [inputMode, sqlCodeInput, pythonCodeInput, wsTables]);

  /** Handle file upload for reference table import. */
  const handleFileUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !sessionId) return;

      const formData = new FormData();
      formData.append('file', file);
      setUploadStatus(`Uploading ${file.name}...`);

      try {
        const { apiClient } = await import('../../services/apiClient');
        const res = await apiClient.post(
          `/api/v1/agents/sessions/${sessionId}/upload`,
          formData,
          // Large file profiling can exceed 30s; allow 3 minutes.
          { timeout: 180000 },
        );
        const data = res.data;
        if (res.status >= 200 && res.status < 300) {
          // Build upload context — stored so it's prepended when the user sends
          let ctx: string;
          if (data.sheets && data.sheets.length > 1) {
            const sheetList = data.sheets
              .map((s: any) => `"${s.table_name}" (${s.row_count} rows, ${s.columns?.length ?? 0} cols)`)
              .join(', ');
            ctx = `[Uploaded "${file.name}" — ${data.sheets.length} sheets: ${sheetList}]`;
          } else {
            ctx = `[Uploaded "${data.table_name}" — ${data.row_count} rows, ${data.columns?.length ?? 0} columns]`;
          }
          setPendingUpload(ctx);
          setUploadStatus(`✓ ${file.name} uploaded — type your message below`);
          setTimeout(() => setUploadStatus(null), 5000);
        } else {
          setUploadStatus(`Upload failed: ${data.detail || data.error}`);
          setTimeout(() => setUploadStatus(null), 4000);
        }
      } catch (err: any) {
        const detail = err?.response?.data?.detail || err?.message || String(err);
        setUploadStatus(`Upload failed: ${detail}`);
        setTimeout(() => setUploadStatus(null), 4000);
      }
      // Reset input so same file can be re-selected
      e.target.value = '';
    },
    [sessionId, sendMessage],
  );

  /** Handle warning option button clicks. */
  const handleOptionClick = useCallback(
    async (optionIndex: number, messageId: string) => {
      const msg = allMessages.find((m) => m.message_id === messageId);
      const options: string[] = msg?.metadata?.options || [];
      const selected = options[optionIndex];
      if (!selected || sendMessage.isLoading) return;

      const tempId = `temp-${Date.now()}`;
      const tempMsg: WorkspaceMessage = {
        message_id: tempId,
        session_id: sessionId,
        role: 'user',
        content: selected,
        metadata: {},
      };
      setOptimisticMessages((prev) => [...prev, tempMsg]);

      try {
        await sendMessage.mutateAsync(selected);
        setOptimisticMessages([]);
      } catch {
        setOptimisticMessages((prev) =>
          prev.map((m) =>
            m.message_id === tempId
              ? { ...m, metadata: { ...m.metadata, failed: true } }
              : m,
          ),
        );
      }
    },
    [allMessages, sessionId, sendMessage],
  );

  /**
   * Handle agent-suggested next-step action click.
   *
   * The pill carries two strings: the short `label` the user actually sees
   * ("Map subjects to ODM"), and the verbose `prompt` the agent needs to
   * disambiguate the request. We send the prompt to the agent but stamp
   * `display_text=label` on the user bubble so the chat history reads as
   * the user's chosen *option*, not the rephrased instruction — otherwise
   * scrolling back through a long session, you can't tell which pill the
   * user picked at each step. Mirrors `handleStarterClick`.
   */
  const handleSuggestionClick = useCallback(
    async (prompt: string, label?: string) => {
      if (sendMessage.isLoading) return;
      // Falls back to plain handleSend when no label is provided (keyboard
      // path historically didn't pass one — kept for safety).
      if (!label) {
        await handleSend(prompt);
        return;
      }

      const tempId = `temp-${Date.now()}`;
      const tempMsg: WorkspaceMessage = {
        message_id: tempId,
        session_id: sessionId,
        role: 'user',
        content: prompt,
        metadata: { display_text: label, from_suggestion: true },
      };
      setOptimisticMessages((prev) => [...prev, tempMsg]);

      try {
        await sendMessage.mutateAsync({ message: prompt, displayText: label });
        setOptimisticMessages([]);
      } catch {
        setOptimisticMessages((prev) =>
          prev.map((m) =>
            m.message_id === tempId
              ? { ...m, metadata: { ...m.metadata, failed: true } }
              : m,
          ),
        );
      }
    },
    [handleSend, sendMessage, sessionId],
  );

  /** Handle starter suggestion chip click — sends the prompt as a message. */
  const handleStarterClick = useCallback(
    async (prompt: string, displayText?: string) => {
      if (sendMessage.isLoading) return;

      const tempId = `temp-${Date.now()}`;
      const tempMsg: WorkspaceMessage = {
        message_id: tempId,
        session_id: sessionId,
        role: 'user',
        content: prompt,
        metadata: displayText ? { display_text: displayText } : {},
      };
      setOptimisticMessages((prev) => [...prev, tempMsg]);

      try {
        await sendMessage.mutateAsync(displayText ? { message: prompt, displayText } : prompt);
        setOptimisticMessages([]);
      } catch {
        setOptimisticMessages((prev) =>
          prev.map((m) =>
            m.message_id === tempId
              ? { ...m, metadata: { ...m.metadata, failed: true } }
              : m,
          ),
        );
      }
    },
    [sessionId, sendMessage],
  );

  // ── Message filtering ─────────────────────────────────────────────

  const displayMessages = useMemo(() => {
    const base =
      optimisticMessages.length > 0
        ? [
          ...allMessages,
          ...optimisticMessages.filter(
            (om) => !allMessages.some((m) => m.content === om.content && m.role === om.role),
          ),
        ]
        : allMessages;

    const nonToolMessages = base.filter((m) => m.role !== 'tool');

    if (!isNodeMode || !selectedNode) {
      return nonToolMessages;
    }

    // Node-scoped: show contextual messages and their responses.
    // A user message with [Re: <node>] prefix is contextual.
    // The agent message(s) immediately following it are its response.
    const nodePrefix = `[Re: ${selectedNode.title || selectedNode.stepType}]`;
    const includedIds = new Set<string>();

    for (let i = 0; i < nonToolMessages.length; i++) {
      const m = nonToolMessages[i];
      const isContextual =
        m.role === 'user' && m.content?.includes(nodePrefix);
      const mentionsNode =
        m.content?.includes(selectedNode.title || '') ||
        m.content?.includes(selectedNode.stepType);

      if (isContextual || mentionsNode) {
        includedIds.add(m.message_id);
        // Also include consecutive agent/system responses that follow
        if (m.role === 'user') {
          for (let j = i + 1; j < nonToolMessages.length; j++) {
            const next = nonToolMessages[j];
            if (next.role === 'user') break; // stop at the next user message
            includedIds.add(next.message_id);
          }
        }
      }
    }

    const nodeMessages = nonToolMessages.filter((m) => includedIds.has(m.message_id));

    return nodeMessages.length > 0
      ? nodeMessages.slice(-20)
      : nonToolMessages.slice(-5);
  }, [allMessages, optimisticMessages, isNodeMode, selectedNode]);

  // ID of the most recent assistant message — used to enable widget
  // interaction only on the latest response (older ones are dimmed).
  const latestAgentMessageId = useMemo(() => {
    for (let i = displayMessages.length - 1; i >= 0; i--) {
      if (displayMessages[i].role === 'assistant') return displayMessages[i].message_id;
    }
    return null;
  }, [displayMessages]);

  // ── Node context data ─────────────────────────────────────────────

  const nodeColor = isNodeMode ? getNodeColor(selectedNode!.stepType) : undefined;
  const nodeIcon = isNodeMode ? getNodeIcon(selectedNode!.stepType) : undefined;
  const statusConfig = isNodeMode ? (STATUS_CONFIG[selectedNode!.status] || STATUS_CONFIG.pending) : undefined;

  // ── Render ────────────────────────────────────────────────────────

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────── */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minHeight: 44,
        }}
      >
        {isNodeMode && selectedNode ? (
          <>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: 0.75,
                bgcolor: nodeColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {nodeIcon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" noWrap fontWeight={700} sx={{ fontSize: 13 }}>
                {selectedNode.title || selectedNode.stepType.replace(/_/g, ' ')}
              </Typography>
              <Chip
                label={statusConfig!.label}
                color={statusConfig!.color}
                size="small"
                variant="outlined"
                sx={{ fontSize: 10, height: 18, fontWeight: 600 }}
              />
            </Box>
            <IconButton size="small" onClick={() => setChatNodeHeaderDismissed(true)}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
            {onCollapse && (
              <Tooltip title="Collapse chat (⌘J)">
                <IconButton size="small" onClick={onCollapse} sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}>
                  <CollapseChatIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </>
        ) : (
          <>
            <ExploreIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1, fontSize: 13 }}>
              Data Exploration
            </Typography>
            <Chip label="Source Layer" size="small" color="primary" variant="outlined" sx={{ fontSize: 10, height: 20, fontWeight: 600 }} />
            {onCollapse && (
              <Tooltip title="Collapse chat (⌘J)">
                <IconButton size="small" onClick={onCollapse} sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}>
                  <CollapseChatIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </>
        )}
      </Box>

      {/* ── Tabs ───────────────────────────────────────────────────── */}
      {hasOperations && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={(_e, v) => setActiveTab(v)}
            sx={{
              minHeight: 30,
              '& .MuiTab-root': { minHeight: 30, py: 0.5, px: 1.5, fontSize: '0.7rem' },
            }}
          >
            <Tab icon={<ChatIcon sx={{ fontSize: 12 }} />} iconPosition="start" label="Chat" value="chat" />
            <Tab icon={<ActivityLogIcon sx={{ fontSize: 12 }} />} iconPosition="start" label="Activity" value="activity" />
          </Tabs>
        </Box>
      )}

      {/* ── Streaming status ───────────────────────────────────────── */}
      {(stream.activeTool || stream.reviewInProgress || stream.agentBusy) && activeTab === 'chat' && (
        <Box
          sx={{
            px: 1.5,
            py: 0.75,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <CircularProgress size={14} thickness={5} />
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 13, fontWeight: 600, letterSpacing: '0.02em' }}>
            {stream.reviewInProgress
              ? 'Reviewing quality...'
              : stream.activeTool
                ? `Running ${stream.activeTool}...`
                : 'Working...'}
          </Typography>
          {stream.reviewInProgress && <ReviewIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
        </Box>
      )}

      {/* ── Mapping feedback context ─────────────────────────────── */}
      {activeTab === 'chat' && activeMappingContext && (
        <Box sx={{ flex: 1, overflow: 'auto', py: 1, px: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Context banner */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.5,
              py: 0.75,
              borderRadius: 2,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
              border: 1,
              borderColor: 'primary.light',
            }}
          >
            <Typography variant="caption" sx={{ flex: 1, fontSize: 11, fontWeight: 500 }} noWrap>
              {activeMappingContext.mapping.source_file}.{activeMappingContext.mapping.source_field}
              {' → '}
              {activeMappingContext.mapping.target_table}.{activeMappingContext.mapping.target_field}
            </Typography>
          </Box>

          {/* Question / Regenerate chips */}
          <Stack direction="row" spacing={0.75} sx={{ px: 0.5 }}>
            <Chip
              label="Question"
              size="small"
              variant={feedbackType === 'question' ? 'filled' : 'outlined'}
              color={feedbackType === 'question' ? 'primary' : 'default'}
              onClick={() => setFeedbackType('question')}
              sx={{ fontSize: 11, height: 24 }}
            />
            <Chip
              label="Regenerate"
              size="small"
              variant={feedbackType === 'regenerate_request' ? 'filled' : 'outlined'}
              color={feedbackType === 'regenerate_request' ? 'secondary' : 'default'}
              onClick={() => setFeedbackType('regenerate_request')}
              sx={{ fontSize: 11, height: 24 }}
            />
          </Stack>

          {/* Feedback history */}
          {feedbackHistory.length === 0 && !feedbackLoading && (
            <Typography variant="caption" color="text.secondary" sx={{ px: 0.5, fontSize: 11 }}>
              Ask a question about this mapping or request AI to regenerate it.
            </Typography>
          )}
          {feedbackHistory.map((entry) => (
            <Box key={entry.id}>
              {/* User message bubble */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                <Box
                  sx={{
                    maxWidth: '85%',
                    backgroundColor: (theme) =>
                      entry.feedback_type === 'question'
                        ? alpha(theme.palette.primary.main, 0.08)
                        : alpha(theme.palette.secondary.main, 0.08),
                    border: '1px solid',
                    borderColor: entry.feedback_type === 'question' ? 'primary.light' : 'secondary.light',
                    borderRadius: '12px 12px 2px 12px',
                    px: 1.5,
                    py: 1,
                  }}
                >
                  <Chip
                    label={entry.feedback_type === 'question' ? 'Question' : 'Regenerate'}
                    size="small"
                    color={entry.feedback_type === 'question' ? 'primary' : 'secondary'}
                    variant="outlined"
                    sx={{ height: 18, fontSize: 10, mb: 0.5, '& .MuiChip-label': { px: 0.75 } }}
                  />
                  <Typography variant="body2" sx={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>
                    {entry.user_message}
                  </Typography>
                </Box>
              </Box>
              {/* AI response bubble — only show when response is available */}
              {entry.llm_response && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 0.5 }}>
                  <Box
                    sx={{
                      maxWidth: '90%',
                      backgroundColor: 'action.hover',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: '12px 12px 12px 2px',
                      px: 1.5,
                      py: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 10, display: 'block', mb: 0.25 }}
                    >
                      AI
                    </Typography>
                    <MarkdownDisplay content={entry.llm_response} sx={{ fontSize: 12 }} />
                  </Box>
                </Box>
              )}
            </Box>
          ))}
          {feedbackLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.5, pl: 1 }}>
              <CircularProgress size={12} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                Thinking...
              </Typography>
            </Box>
          )}
          <div ref={feedbackEndRef} />
        </Box>
      )}

      {/* ── Chat messages ──────────────────────────────────────────── */}
      {activeTab === 'chat' && !activeMappingContext && (
        <Box sx={{ flex: 1, minHeight: 0, position: 'relative', display: 'flex', flexDirection: 'column' }}>
          {/* Expanded progress sheet overlays just the transcript area
              (the strip + input + suggestions remain interactive below)
              and is anchored to this outer positioned wrapper rather
              than the inner scrolling Box — otherwise it scrolls with
              the transcript instead of staying pinned to the bottom. */}
          <ProgressSheet
            progress={stream.progress}
            open={progressExpanded}
            onClose={() => setProgressExpanded(false)}
          />
          <Box sx={{ flex: 1, overflow: 'auto', py: 1, px: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {loadingMessages && allMessages.length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={20} />
            </Box>
          )}

          {/* System start message (only in general mode) */}
          {!isNodeMode && (allMessages.length > 0 || optimisticMessages.length > 0 || mappingActive) && (
            <ChatMessage
              message={{
                message_id: '__system_start',
                session_id: sessionId,
                role: 'system',
                content: mappingActive && allMessages.length === 0
                  ? mappingMode === 'iterative'
                    ? 'Iterative mapping started. The agent will map each target table conversationally.'
                    : mappingTargetCount
                      ? `Mapping pipeline complete — ${mappingTargetCount} target tables generated. Click a target table or "Open Editor" to review field mappings.`
                      : 'Mapping pipeline started. Target table views will appear on the canvas as mappings are generated.'
                  : 'Session started. Source data loaded into workspace.',
                metadata: {},
              }}
            />
          )}

          {/* Starter suggestions (shown when session is fresh, no messages yet, no mapping active) */}
          {!isNodeMode && !loadingMessages && allMessages.length === 0 && optimisticMessages.length === 0 && !mappingActive && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4, px: 1 }}>
              <Box sx={{ textAlign: 'center', mb: 1 }}>
                <ExploreIcon sx={{ fontSize: 32, color: 'primary.main', mb: 0.5 }} />
                <Typography variant="subtitle2" color="text.primary">
                  What would you like to do?
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Choose a starting point or type your own request below
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                {starterSuggestions.map((s) => (
                  <Box
                    key={s.label}
                    onClick={() => {
                      if (s.action) {
                        s.action();
                      } else if (s.prompt) {
                        handleStarterClick(s.prompt, s.label);
                      }
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      px: 1.5,
                      py: 1.25,
                      borderRadius: 2,
                      border: 1,
                      borderColor: 'divider',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      '&:hover': {
                        borderColor: s.color,
                        bgcolor: alpha(s.color, 0.04),
                        boxShadow: `0 0 0 1px ${alpha(s.color, 0.2)}`,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 32,
                        height: 32,
                        borderRadius: 1.5,
                        bgcolor: alpha(s.color, 0.1),
                        color: s.color,
                        flexShrink: 0,
                      }}
                    >
                      {s.icon}
                    </Box>
                    <Typography variant="body2" fontWeight={500} sx={{ fontSize: 13 }}>
                      {s.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Empty state for node mode */}
          {isNodeMode && displayMessages.length === 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
              <Typography variant="caption" color="text.disabled">
                Ask a question about this step
              </Typography>
            </Box>
          )}

          {displayMessages.map((msg) => (
            <ChatMessage
              key={msg.message_id}
              message={msg}
              onOptionClick={handleOptionClick}
              onSuggestionClick={handleSuggestionClick}
              isLatestAgentMessage={msg.message_id === latestAgentMessageId}
              onDismissFailure={
                msg.metadata?.failed
                  ? () => setOptimisticMessages((prev) => prev.filter((m) => m.message_id !== msg.message_id))
                  : undefined
              }
              onOpenEditor={onOpenEditor}
              onFocusNode={onFocusNode}
            />
          ))}

          {/* Export progress */}
          {showExportProgress && (
            <Box
              sx={{
                mx: 1,
                my: 1,
                p: 1.5,
                borderRadius: 2,
                border: 1,
                borderColor: alpha('#7c3aed', 0.25),
                bgcolor: alpha('#7c3aed', 0.04),
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <CircularProgress size={16} thickness={5} sx={{ color: '#7c3aed' }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" fontWeight={600} sx={{ fontSize: 11, display: 'block', color: '#5b21b6' }}>
                  {exportProgressLabel}
                </Typography>
                <LinearProgress
                  variant={exportProgressPercent != null && exportProgressPercent > 0 ? 'determinate' : 'indeterminate'}
                  value={exportProgressPercent ?? 0}
                  sx={{
                    mt: 0.5,
                    height: 4,
                    borderRadius: 2,
                    bgcolor: alpha('#7c3aed', 0.1),
                    '& .MuiLinearProgress-bar': { borderRadius: 2, bgcolor: '#7c3aed' },
                  }}
                />
              </Box>
              {exportProgressPercent != null && exportProgressPercent > 0 && (
                <Typography variant="caption" sx={{ fontSize: 10, flexShrink: 0, color: '#7c3aed', fontWeight: 600 }}>
                  {exportProgressPercent}%
                </Typography>
              )}
            </Box>
          )}

          {/* Pipeline progress (profiling, semantic summary, etc.) */}
          {showPipelineProgress && pipelineProgress && (
            <Box
              sx={{
                mx: 1,
                my: 1,
                p: 1.5,
                borderRadius: 2,
                border: 1,
                borderColor: 'divider',
                bgcolor: alpha(theme.palette.info.main, 0.03),
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <CircularProgress size={16} thickness={5} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" fontWeight={600} sx={{ fontSize: 11, display: 'block' }}>
                  {pipelineProgress.logLine}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={pipelineProgress.progressPercent}
                  sx={{
                    mt: 0.5,
                    height: 4,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    '& .MuiLinearProgress-bar': { borderRadius: 2 },
                  }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, flexShrink: 0 }}>
                {pipelineProgress.progressPercent}%
              </Typography>
            </Box>
          )}

          {(sendMessage.isLoading || isBusy || stream.agentBusy) && !stream.activeTool && !showPipelineProgress && !showExportProgress && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.5, pl: 1 }}>
              <CircularProgress size={12} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                Working...
              </Typography>
            </Box>
          )}

          <div ref={messagesEndRef} />
          </Box>
        </Box>
      )}

      {/* ── Activity Log tab ───────────────────────────────────────── */}
      {activeTab === 'activity' && (
        <Box sx={{ flex: 1, overflow: 'auto', py: 0.5 }}>
          <ActivityLogView
            operations={operations}
            sessionId={sessionId}
            onDiscard={(opId) => discard.mutate(opId)}
            onSetExploratory={(opId, exploratory) => setExploratory.mutate({ operationId: opId, exploratory })}
          />
        </Box>
      )}

      {/* ── Upload status ─────────────────────────────────────────── */}
      {uploadStatus && (
        <Box sx={{ px: 1.5, py: 0.5, bgcolor: 'info.50' }}>
          <Typography variant="caption" color="info.main" sx={{ fontSize: 11 }}>
            {uploadStatus}
          </Typography>
        </Box>
      )}

      {/* ── Pending upload chip ──────────────────────────────────── */}
      {pendingUpload && (
        <Box sx={{ px: 1.5, py: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Chip
            label={pendingUpload.replace(/^\[|\]$/g, '')}
            size="small"
            color="primary"
            variant="outlined"
            onDelete={() => setPendingUpload(null)}
            sx={{ fontSize: 11, maxWidth: '100%' }}
          />
        </Box>
      )}

      {/* ── Input area ─────────────────────────────────────────────── */}
      <Divider />

      {/* Contextual suggestions above input */}
      {contextualSuggestions.length > 0 && (
        <ContextualSuggestions
          suggestions={contextualSuggestions}
          onSendMessage={(msg) => handleSend(msg)}
        />
      )}

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        accept=".csv,.tsv,.xlsx,.xls,.parquet"
        style={{ display: 'none' }}
      />

      {/* Mode selector — also hosts the inline progress chip so the
          chat surface doesn't gain a separate row just for the heartbeat. */}
      <Box
        sx={{ px: 1, pt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}
        onKeyDown={(e) => {
          const mod = e.metaKey || e.ctrlKey;
          if (mod && e.key === '1') { e.preventDefault(); setInputMode('chat'); }
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
          <ProgressStrip
            progress={stream.progress}
            expanded={progressExpanded}
            onToggle={() => setProgressExpanded((v) => !v)}
          />
        </Box>
        <IconButton
          size="small"
          onClick={() => fileInputRef.current?.click()}
          disabled={sendMessage.isLoading}
          title="Upload reference table"
          sx={{
            width: 26,
            height: 26,
            color: 'text.secondary',
            '&.Mui-disabled': {
              cursor: 'not-allowed',
              pointerEvents: 'auto',
            },
          }}
        >
          <AttachFileIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>

      {/* Chat mode input */}
      {inputMode === 'chat' && (
        <Box sx={{ p: 1, pt: 0.5, display: 'flex', gap: 0.5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={
              activeMappingContext
                ? feedbackType === 'question'
                  ? 'e.g. Why was this transformation chosen?'
                  : 'e.g. Use TRIM and UPPER instead'
                : isNodeMode && selectedNode
                  ? `Ask about ${selectedNode.title || selectedNode.stepType}...`
                  : 'Ask about the data or request a transformation...'
            }
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={sendMessage.isLoading || feedbackLoading || chatLocked}
            multiline
            maxRows={3}
            sx={{
              '& .MuiInputBase-root': {
                fontSize: '0.8rem',
                borderRadius: 3,
                bgcolor: 'grey.50',
              },
              '& .MuiInputBase-root.Mui-disabled': {
                cursor: 'not-allowed',
              },
              '& .MuiInputBase-input.Mui-disabled': {
                cursor: 'not-allowed',
              },
            }}
          />
          <IconButton
            color="primary"
            onClick={() => handleSend()}
            disabled={!input.trim() || sendMessage.isLoading || feedbackLoading || chatLocked}
            size="small"
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              width: 32,
              height: 32,
              '&:hover': { bgcolor: 'primary.dark' },
              '&.Mui-disabled': {
                bgcolor: 'grey.300',
                color: 'grey.500',
                cursor: 'not-allowed',
                pointerEvents: 'auto',
              },
            }}
          >
            <SendIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      )}

      {/* SQL/Python inline editors removed — use Cmd+K command palette or FAB instead */}
    </Box>
  );
};

export default ChatPanel;
